import React, { useEffect, useMemo, useState } from "react";
import { useGetIdentity, useUpdate, useDataProvider, useInvalidate } from "@refinedev/core";
import { Alert, Card, Form, Input, Select, Button, message, Row, Col, Tabs, Typography, Switch, InputNumber, Grid } from "antd";
import { UserOutlined, BellOutlined, BankOutlined, SettingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { USER_KEY } from "../../authProvider";
import { IUser } from "../../models/user";
import NotificationPreferences from "../notifications/notification-preferences";
import { getCountryOptions } from "../../utils/countries";
import { useCustomer } from "../../hooks/auth";
import { AccountType } from "../../models/account";
import { useSelectValues } from "../accounts/values";
import { PreferencesTab } from "./PreferencesTab";

const { TabPane } = Tabs;
const { Title } = Typography;

export const SettingsPage: React.FC = () => {
    const { data: user } = useGetIdentity<IUser>();
    const { mutate: update } = useUpdate();
    const invalidate = useInvalidate();
    const { t, i18n } = useTranslation();
    const countryOptions = useMemo(
        () => getCountryOptions(i18n.language),
        [i18n.language],
    );
    const [form] = Form.useForm();
    const [accountForm] = Form.useForm();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get("tab");
    const [activeTab, setActiveTab] = useState(tabParam || "profile");
    const breakpoint = Grid.useBreakpoint();
    const isMobile = typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg;
    const isCustomer = useCustomer();
    const { accountTypes } = useSelectValues();
    const accountType = Form.useWatch("type", accountForm);

    useEffect(() => {
        if (tabParam) {
            setActiveTab(tabParam);
        }
    }, [tabParam]);

    const handleTabChange = (key: string) => {
        setActiveTab(key);
        const next = new URLSearchParams(searchParams);
        next.set("tab", key);
        setSearchParams(next, { replace: true });
    };

    const onFinish = (values: any) => {
        update(
            {
                resource: "users",
                values: {
                    ...values,
                    id: user?.id,
                },
                id: user?.id,
            },
            {
                onSuccess: (response) => {
                    message.success(t("profile.messages.updateSuccess"));
                    localStorage.setItem(USER_KEY, JSON.stringify(response.data));
                    // Refetch identity instead of reloading: a reload would throw
                    // away the active tab and the ?tab= deep link.
                    invalidate({ resource: "users", invalidates: ["all"] });
                    form.setFieldsValue(response.data);
                },
                onError: () => {
                    message.error(t("profile.messages.updateError"));
                },
            }
        );
    };

    useEffect(() => {
        form.setFieldsValue({
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: user?.email,
        });
    }, [user]);

    useEffect(() => {
        if (user?.account) {
            accountForm.setFieldsValue({
                type: user.account.type,
                name: user.account.name,
                firstName: user.account.firstName,
                lastName: user.account.lastName,
                legalId: user.account.legalId,
                address: user.account.address,
                zipCode: user.account.zipCode,
                city: user.account.city,
                country: user.account.country,
                phone: user.account.phone,
                email: user.account.email,
                applyVat: user.account.applyVat,
                customVatRate: user.account.customVatRate,
            });
        }
    }, [user, accountForm]);

    const onAccountFinish = (values: any) => {
        if (isCustomer) return;
        if (!user?.account?.id) return;

        console.log("Update Values from account form", values);

        update(
            {
                resource: "accounts",
                values: {
                    ...values,
                    id: user.account.id,
                },
                id: user.account.id,
            },
            {
                onSuccess: async () => {
                    message.success(t("accounts.messages.updateSuccess"));
                    // Fetch updated user data and update localStorage
                    if (user?.id) {
                        try {
                            const updatedUserData = {
                                ...user,
                                account: values,
                            };
                            console.log("Updated user data", updatedUserData);
                            if (updatedUserData) {
                                localStorage.setItem(USER_KEY, JSON.stringify(updatedUserData));
                                // Reload to reflect changes
                                //window.location.reload();
                            }
                        } catch (error) {
                            console.error("Failed to fetch updated user data:", error);
                        }
                    }
                },
                onError: () => {
                    message.error(t("accounts.messages.updateError"));
                },
            }
        );
    };

    return (
        <Card style={{ maxWidth: 800, margin: "40px auto" }} className={isMobile ? "bg-transparent border-none" : ""}>
            <Title level={3}>{t("settings.myAccount")}</Title>
            <Tabs activeKey={activeTab} onChange={handleTabChange}>
                <TabPane tab={t("settings.tabs.profile", "Profile")} key="profile" icon={<UserOutlined />}>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="firstName"
                                    label={t("profile.fields.firstName")}
                                    rules={[{ required: true }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="lastName"
                                    label={t("profile.fields.lastName")}
                                    rules={[{ required: true }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="email"
                                    label={t("profile.fields.email")}
                                    rules={[
                                        { required: true },
                                        { type: "email" }
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item>
                            <Button type="primary" htmlType="submit">
                                {t("profile.actions.save")}
                            </Button>
                        </Form.Item>
                    </Form>
                </TabPane>

                {user?.account && (
                    <TabPane tab={t("settings.tabs.account", "Account")} key="account" icon={<BankOutlined />}>
                        <Form
                            form={accountForm}
                            layout="vertical"
                            onFinish={onAccountFinish}
                        >
                            {isCustomer && (
                                <Alert
                                    type="info"
                                    showIcon
                                    message={t("settings.account.readOnlyHint")}
                                    className="!mb-4"
                                />
                            )}
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item
                                        name="type"
                                        label={t("accounts.fields.type")}
                                        rules={[{ required: true }]}
                                    >
                                        <Select
                                            options={accountTypes}
                                            disabled={isCustomer}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {accountType === AccountType.company ? (
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="name"
                                            label={t("accounts.fields.name")}
                                            rules={[{ required: true }]}
                                        >
                                            <Input disabled={isCustomer} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="legalId"
                                            label={t("accounts.fields.legalId")}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: t(
                                                        "accounts.validation.legalIdRequiredForCompany",
                                                    ),
                                                },
                                            ]}
                                        >
                                            <Input disabled={isCustomer} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            ) : (
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="firstName"
                                            label={t("accounts.fields.firstName")}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: t(
                                                        "accounts.validation.firstNameRequiredForIndividual",
                                                    ),
                                                },
                                            ]}
                                        >
                                            <Input disabled={isCustomer} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="lastName"
                                            label={t("accounts.fields.lastName")}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: t(
                                                        "accounts.validation.lastNameRequiredForIndividual",
                                                    ),
                                                },
                                            ]}
                                        >
                                            <Input disabled={isCustomer} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            )}
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item
                                        name="address"
                                        label={t("accounts.fields.address")}
                                    >
                                        <Input disabled={isCustomer} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="zipCode"
                                        label={t("accounts.fields.zipCode")}
                                    >
                                        <Input disabled={isCustomer} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="city"
                                        label={t("accounts.fields.city")}
                                    >
                                        <Input disabled={isCustomer} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="country"
                                        label={t("accounts.fields.country")}
                                    >
                                        <Select
                                            showSearch
                                            optionFilterProp="label"
                                            allowClear
                                            disabled={isCustomer}
                                            options={countryOptions}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="phone"
                                        label={t("accounts.fields.phone")}
                                    >
                                        <Input disabled={isCustomer} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="email"
                                        label={t("accounts.fields.email")}
                                        rules={[
                                            { required: true },
                                            { type: "email" }
                                        ]}
                                    >
                                        <Input disabled={isCustomer} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label={t("accounts.fields.applyVat")}
                                        name="applyVat"
                                        valuePropName="checked"
                                    >
                                        <Switch disabled={true} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label={t("accounts.fields.customVatRate")}
                                        name="customVatRate"
                                    >
                                        <InputNumber
                                            min={0}
                                            max={100}
                                            precision={2}
                                            addonAfter="%"
                                            disabled={true}
                                            className="w-full"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {!isCustomer && (
                                <Form.Item>
                                    <Button type="primary" htmlType="submit">
                                        {t("profile.actions.save")}
                                    </Button>
                                </Form.Item>
                            )}
                        </Form>
                    </TabPane>
                )}
                <TabPane tab={t("settings.tabs.notifications")} key="notifications" icon={<BellOutlined />}>
                    <NotificationPreferences />
                </TabPane>
                <TabPane tab={t("settings.tabs.preferences", "Preferences")} key="preferences" icon={<SettingOutlined />}>
                    <PreferencesTab />
                </TabPane>
            </Tabs>
        </Card>
    );
};

export default SettingsPage; 